import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCart } from '../../context/CartContext';
import Button from '../ui/Button';
import Badge from '../ui/Badge';

const CartDrawer = () => {
  const { cart, isCartOpen, toggleCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [checkoutStep, setCheckoutStep] = useState('cart'); // cart, shipping, success

  const total = getCartTotal();
  const shipping = total > 50 ? 0 : 5.99;

  const handleCheckout = () => {
    setCheckoutStep('shipping');
    setTimeout(() => {
      setCheckoutStep('success');
      clearCart();
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleCart}
            className="fixed inset-0 bg-ink-navy/60 backdrop-blur-sm z-[60]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col border-l-4 border-ink-navy"
          >
            {/* Header */}
            <div className="p-6 border-b-4 border-ink-navy flex justify-between items-center bg-slate-50">
              <h2 className="text-2xl font-black text-ink-navy uppercase italic">Lab Equipment</h2>
              <button 
                onClick={toggleCart}
                className="w-10 h-10 rounded-full border-2 border-ink-navy flex items-center justify-center hover:bg-lemon-yellow transition-colors"
                aria-label="Close cart"
              >
                <SafeIcon name="X" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-grow overflow-y-auto p-6 bg-notebook">
              {checkoutStep === 'cart' && (
                <>
                  {cart.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6 border-2 border-dashed border-slate-300">
                        <SafeIcon name="ShoppingCart" className="text-4xl text-slate-300" />
                      </div>
                      <h3 className="text-xl font-black text-ink-navy mb-2">Your beaker is empty!</h3>
                      <p className="text-slate-500 font-bold mb-8 italic">Add some kits to start your next project.</p>
                      <Button onClick={toggleCart} variant="primary">Start Exploring</Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {cart.map((item) => (
                        <div key={item.id} className="flex gap-4 p-4 bg-white border-2 border-ink-navy rounded-2xl shadow-[4px_4px_0_0_#1e1b4b]">
                          <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg border-2 border-ink-navy" />
                          <div className="flex-grow">
                            <h4 className="font-black text-ink-navy leading-tight mb-1">{item.name}</h4>
                            <p className="text-xs font-bold text-sky-blue uppercase mb-3">{item.category}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 border-2 border-ink-navy rounded-lg p-1 bg-slate-50">
                                <button onClick={() => updateQuantity(item.id, -1)} className="w-6 h-6 flex items-center justify-center hover:text-sky-blue"><SafeIcon name="Minus" /></button>
                                <span className="font-black text-sm">{item.quantity}</span>
                                <button onClick={() => updateQuantity(item.id, 1)} className="w-6 h-6 flex items-center justify-center hover:text-sky-blue"><SafeIcon name="Plus" /></button>
                              </div>
                              <span className="font-black text-lg text-ink-navy">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {checkoutStep === 'shipping' && (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-20 h-20 border-4 border-sky-blue border-t-transparent rounded-full animate-spin" />
                  <h3 className="text-2xl font-black text-ink-navy uppercase italic">Processing Order...</h3>
                  <p className="text-slate-500 font-bold">Verifying lab inventory and calculating coordinates.</p>
                </div>
              )}

              {checkoutStep === 'success' && (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-6 border-4 border-emerald-500"
                  >
                    <SafeIcon name="Check" className="text-5xl text-emerald-600" />
                  </motion.div>
                  <h3 className="text-2xl font-black text-ink-navy uppercase italic mb-2">Success!</h3>
                  <p className="text-slate-500 font-bold mb-8">Your equipment is being prepared. Check your email for the shipment tracker.</p>
                  <Button onClick={() => { setCheckoutStep('cart'); toggleCart(); }} variant="primary">Continue Discovering</Button>
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && checkoutStep === 'cart' && (
              <div className="p-6 border-t-4 border-ink-navy bg-slate-50 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between font-bold text-slate-500">
                    <span>Subtotal</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-slate-500">
                    <span>Shipping</span>
                    <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between text-xl font-black text-ink-navy pt-2 border-t-2 border-dashed border-slate-300">
                    <span>Total</span>
                    <span>${(total + shipping).toFixed(2)}</span>
                  </div>
                </div>
                <Button onClick={handleCheckout} className="w-full text-lg py-4" variant="primary">
                  Begin Secure Checkout
                </Button>
                <p className="text-[10px] text-center font-bold text-slate-400 uppercase tracking-widest">
                  <SafeIcon name="Lock" className="inline mr-1" /> Encrypted Transaction Platform
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;